/**
 * Compose phase — the hero.
 *
 * Two columns: the offer and the input on the left, a real running booth on
 * the right. The headline is the booth URL rather than a claim about AI,
 * because the short shareable link is the part of this product nobody else
 * has, and the device answers "what do I actually get" before a word is read.
 *
 * Left column: URL headline → prompt textarea (+ logo / reference
 * attachments) → the starter rail of live booths → vibe chips (curated
 * palettes that steer generation via a prompt suffix). One tap hands off to
 * the theater.
 *
 * With an empty showcase this collapses to a single centred column with the
 * old text starter chips — no dead space, no broken images.
 */

import React, { useCallback, useRef, useState } from 'react'
import {
  FaImage,
  FaTimes,
  FaMagic,
  FaPaperclip,
  FaSpinner,
  FaArrowLeft,
  FaLink
} from 'react-icons/fa'
import { useOnboardingStore, type AttachmentRole } from '../onboardingStore'
import { THEME_TEMPLATES, type ThemeTemplate } from '../themeTemplates'
import { useCopy } from '../regionCopy'
import { useShowcase } from '../useShowcase'
import { inspectLink } from '../onboardingApi'
import BoothRail from './BoothRail'
import HeroBooth from './HeroBooth'
import UrlHeadline from './UrlHeadline'

const MAX_LOGO_BYTES = 2 * 1024 * 1024
const MAX_ATTACHMENTS = 3

const ROLE_ORDER: AttachmentRole[] = ['auto', 'style', 'literal']

interface PromptComposerProps {
  onGenerate: () => void
  /** True while the clarify questions are being prepared. */
  busy?: boolean
  /**
   * Signed-out visitors see "Sign in & build it" — generation requires an
   * account (2026-08-16), and the button must say so BEFORE the Google popup
   * appears, not surprise the visitor with it.
   */
  signedOut?: boolean
  /**
   * Set when the creator already has a generated booth and reached this
   * screen from "Edit prompt". Without it this page was a one-way door: the
   * only forward action spent a generation and there was nothing to press to
   * get back to the design.
   */
  onBackToBooth?: () => void
}

export const PromptComposer: React.FC<PromptComposerProps> = ({
  onGenerate,
  busy,
  signedOut,
  onBackToBooth
}) => {
  const prompt = useOnboardingStore((s) => s.prompt)
  const setPrompt = useOnboardingStore((s) => s.setPrompt)
  const logoName = useOnboardingStore((s) => s.logoName)
  const setLogo = useOnboardingStore((s) => s.setLogo)
  const attachments = useOnboardingStore((s) => s.attachments)
  const addAttachment = useOnboardingStore((s) => s.addAttachment)
  const removeAttachment = useOnboardingStore((s) => s.removeAttachment)
  const setAttachmentRole = useOnboardingStore((s) => s.setAttachmentRole)
  const error = useOnboardingStore((s) => s.error)

  const slug = useOnboardingStore((s) => s.slug)
  const setSlug = useOnboardingStore((s) => s.setSlug)
  const remaining = useOnboardingStore((s) => s.remaining)
  const c = useCopy()
  const ROLE_LABELS: Record<AttachmentRole, string> = {
    auto: c.roleAuto,
    style: c.roleStyle,
    literal: c.roleLiteral
  }

  // Rebuilding replaces the existing design and spends one of three
  // generations. Both facts used to be invisible until after it happened.
  const rebuilding = Boolean(onBackToBooth)
  const fullGensLeft = remaining?.fullGenerations ?? 0

  const { placeholder, starterPrompts } = c
  const { booths, current, index, setPaused } = useShowcase()

  const [vibeId, setVibeId] = useState<string | null>(null)
  const [logoError, setLogoError] = useState<string | null>(null)
  const [pickedSlug, setPickedSlug] = useState<string | null>(null)
  const [link, setLink] = useState('')
  const [linkBusy, setLinkBusy] = useState(false)
  const [linkNote, setLinkNote] = useState<{ tone: 'ok' | 'warn'; text: string } | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const attachRef = useRef<HTMLInputElement>(null)

  // Identity-stable so UrlHeadline's effect doesn't re-fire every render.
  const handleEditingChange = useCallback((editing: boolean) => setPaused(editing), [setPaused])

  const handleRailPick = (seed: string): void => {
    setPrompt(seed)
    setPickedSlug(booths.find((b) => (b.prompt || b.title) === seed)?.slug ?? null)
  }

  const handleAttachmentFile = (file: File | undefined): void => {
    setLogoError(null)
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setLogoError(c.errNotImage)
      return
    }
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError(c.errReferenceTooBig)
      return
    }
    const reader = new FileReader()
    reader.onload = () =>
      addAttachment({ dataUrl: String(reader.result), name: file.name, role: 'auto' })
    reader.readAsDataURL(file)
  }

  const cycleRole = (index: number): void => {
    const current = attachments[index]?.role ?? 'auto'
    const next = ROLE_ORDER[(ROLE_ORDER.indexOf(current) + 1) % ROLE_ORDER.length]
    setAttachmentRole(index, next)
  }

  const applyVibe = (t: ThemeTemplate): void => {
    const marker = /\n\nPalette direction:.*$/s
    const base = prompt.replace(marker, '')
    if (vibeId === t.id) {
      setVibeId(null)
      setPrompt(base)
      return
    }
    setVibeId(t.id)
    setPrompt(
      `${base}\n\nPalette direction: ${t.name} — ${t.mood}. Primary ${t.primaryColor} on background ${t.backgroundColor}.`
    )
  }

  const handleLogoFile = (file: File | undefined): void => {
    setLogoError(null)
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setLogoError(c.errNotImage)
      return
    }
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError(c.errLogoTooBig)
      return
    }
    const reader = new FileReader()
    reader.onload = () => setLogo(String(reader.result), file.name)
    reader.readAsDataURL(file)
  }

  /**
   * Turn a pasted link into design material: its title and description join
   * the prompt, its images become style references. Everything downstream —
   * clarify questions included — then works exactly as if the creator had
   * typed and attached it all themselves.
   */
  const handleReadLink = async (): Promise<void> => {
    const url = link.trim()
    if (!url || linkBusy) return
    setLinkBusy(true)
    setLinkNote(null)
    try {
      const preview = await inspectLink(url)
      if (!preview.readable) {
        setLinkNote({
          tone: 'warn',
          text: preview.reason === 'private_or_login_required' ? c.linkPrivate : c.linkUnreadable
        })
        return
      }
      const context = [preview.title, preview.description].filter(Boolean).join(' — ').slice(0, 400)
      if (context) {
        const base = prompt.trim()
        setPrompt(base ? `${base}

Reference: ${context}` : context)
      }
      let added = 0
      for (const dataUrl of preview.images.slice(0, MAX_ATTACHMENTS - attachments.length)) {
        addAttachment({ dataUrl, name: preview.siteName || 'link', role: 'style' })
        added++
      }
      setLinkNote({ tone: 'ok', text: c.linkFound(added) })
      setLink('')
    } finally {
      setLinkBusy(false)
    }
  }

  const canGenerate = prompt.trim().length >= 3

  const hasShowcase = booths.length > 0

  return (
    <div
      className={`mx-auto grid w-full gap-x-12 gap-y-8 px-6 py-12 sm:py-16 ${
        hasShowcase ? 'max-w-5xl lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start' : 'max-w-2xl'
      }`}
    >
      <div className={`flex min-w-0 flex-col gap-6 ${hasShowcase ? '' : 'text-center'}`}>
        {rebuilding && (
          <div
            className={`flex flex-wrap items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 ${
              hasShowcase ? '' : 'justify-center'
            }`}
          >
            <button
              type="button"
              onClick={onBackToBooth}
              className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs font-semibold text-whiteout transition-colors hover:bg-white/20"
            >
              <FaArrowLeft className="text-[10px]" /> {c.backToMyBooth}
            </button>
            <span className="text-xs text-whiteout/50">
              {c.rebuildWarning}{' '}
              <b className="font-semibold text-whiteout/70">{c.rebuildLeft(fullGensLeft)}</b>{' '}
              {c.rebuildKeeps}
            </span>
          </div>
        )}

        <header className={`space-y-3 ${hasShowcase ? '' : 'text-center'}`}>
          <UrlHeadline
            rotatingSlug={current?.slug ?? null}
            claimedSlug={slug}
            onClaim={setSlug}
            onEditingChange={handleEditingChange}
          />
          <p className={`text-whiteout/60 ${hasShowcase ? 'max-w-md' : 'mx-auto max-w-md'}`}>
            {c.heroSub}
          </p>
        </header>

        {/* Chat box */}
        <div className="relative rounded-3xl border border-white/15 bg-white/[0.05] p-4 shadow-2xl backdrop-blur focus-within:border-purpleSnail/70">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={placeholder}
            rows={4}
            maxLength={1000}
            className="w-full resize-none bg-transparent text-base text-whiteout placeholder-whiteout/30 outline-none"
          />

          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            {/* Logo attach */}
            <div className="flex items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleLogoFile(e.target.files?.[0])}
              />
              <input
                ref={attachRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  handleAttachmentFile(e.target.files?.[0])
                  if (attachRef.current) attachRef.current.value = ''
                }}
              />
              {attachments.length < MAX_ATTACHMENTS && (
                <button
                  type="button"
                  onClick={() => attachRef.current?.click()}
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 px-3 py-1.5 text-xs text-whiteout/60 transition-colors hover:border-white/30 hover:text-whiteout"
                >
                  <FaPaperclip /> {c.addReference}
                </button>
              )}
              {logoName ? (
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs text-whiteout/80">
                  <FaImage className="text-blueSparkle" /> {logoName}
                  <button
                    type="button"
                    onClick={() => {
                      setLogo(null, null)
                      if (fileRef.current) fileRef.current.value = ''
                    }}
                    aria-label={c.removeLogo}
                    className="text-whiteout/50 hover:text-whiteout"
                  >
                    <FaTimes />
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 px-3 py-1.5 text-xs text-whiteout/60 transition-colors hover:border-white/30 hover:text-whiteout"
                >
                  <FaImage /> {c.attachLogo}
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={onGenerate}
              disabled={!canGenerate || busy || (rebuilding && fullGensLeft <= 0)}
              className="inline-flex items-center gap-2 rounded-full bg-blueSparkle px-6 py-2.5 text-sm font-semibold text-whiteout shadow-lg transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {busy ? <FaSpinner className="animate-spin" /> : <FaMagic />}
              {busy
                ? c.preparing
                : rebuilding
                  ? c.replaceMyDesign
                  : signedOut
                    ? c.buildItSignedOut
                    : c.buildIt}
            </button>
          </div>
        </div>

        {/* Start from a link */}
        <div className="space-y-2">
          <p
            className={`text-xs uppercase tracking-wide text-whiteout/40 ${
              hasShowcase ? '' : 'text-center'
            }`}
          >
            {c.linkLabel}
          </p>
          <div className="flex items-center gap-2 rounded-2xl border border-white/15 bg-white/[0.04] px-3 py-2 focus-within:border-blueSparkle">
            <FaLink className="shrink-0 text-xs text-whiteout/30" />
            <input
              value={link}
              onChange={(e) => setLink(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleReadLink()
              }}
              placeholder={c.linkPlaceholder}
              maxLength={2048}
              className="min-w-0 flex-1 bg-transparent text-sm text-whiteout placeholder-whiteout/30 outline-none"
            />
            <button
              type="button"
              onClick={() => void handleReadLink()}
              disabled={!link.trim() || linkBusy}
              className="inline-flex shrink-0 items-center gap-2 rounded-full border border-white/15 px-3 py-1.5 text-xs text-whiteout/70 transition-colors hover:border-blueSparkle/60 hover:text-whiteout disabled:opacity-40"
            >
              {linkBusy && <FaSpinner className="animate-spin" />}
              {linkBusy ? c.linkReading : c.linkRead}
            </button>
          </div>
          {linkNote && (
            <p
              className={`text-xs ${
                linkNote.tone === 'ok' ? 'text-blueSparkle' : 'text-amber-300/80'
              } ${hasShowcase ? '' : 'text-center'}`}
            >
              {linkNote.text}
            </p>
          )}
        </div>

        {/* Attached references with tap-to-cycle roles */}
        {attachments.length > 0 && (
          <div className={`flex flex-wrap gap-3 ${hasShowcase ? '' : 'justify-center'}`}>
            {attachments.map((a, i) => (
              <div
                key={`${a.name}-${i}`}
                className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.04] p-2"
              >
                <img src={a.dataUrl} alt={a.name} className="h-10 w-10 rounded-lg object-cover" />
                <div className="flex flex-col items-start gap-1">
                  <span className="max-w-[120px] truncate text-[11px] text-whiteout/70">
                    {a.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => cycleRole(i)}
                    title={c.roleHint}
                    className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                      a.role === 'literal'
                        ? 'bg-blueSparkle/30 text-blueSparkle'
                        : a.role === 'style'
                          ? 'bg-white/10 text-whiteout/70'
                          : 'bg-white/5 text-whiteout/40'
                    }`}
                  >
                    {ROLE_LABELS[a.role]}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => removeAttachment(i)}
                  aria-label={c.removeReference}
                  className="ml-1 text-whiteout/40 hover:text-whiteout"
                >
                  <FaTimes />
                </button>
              </div>
            ))}
          </div>
        )}

        {(logoError || error) && (
          <p className={`text-sm text-red-400 ${hasShowcase ? '' : 'text-center'}`}>
            {logoError || error}
          </p>
        )}

        <BoothRail
          booths={booths}
          starterPrompts={starterPrompts}
          onPick={handleRailPick}
          activeSlug={pickedSlug}
        />

        {/* Vibe chips (optional palette steer) */}
        <div className="space-y-2">
          <p
            className={`text-xs uppercase tracking-wide text-whiteout/40 ${
              hasShowcase ? '' : 'text-center'
            }`}
          >
            {c.pickAVibe}
          </p>
          <div className={`flex flex-wrap gap-2 ${hasShowcase ? '' : 'justify-center'}`}>
            {THEME_TEMPLATES.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => applyVibe(t)}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                  vibeId === t.id
                    ? 'border-blueSparkle text-whiteout'
                    : 'border-white/10 text-whiteout/60 hover:border-white/30'
                }`}
              >
                <span
                  className="h-3.5 w-3.5 rounded-full ring-1 ring-white/20"
                  style={{ background: t.gradient }}
                />
                {t.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {hasShowcase && (
        <div className="justify-self-center lg:sticky lg:top-24">
          <HeroBooth booth={current} booths={booths} index={index} />
        </div>
      )}
    </div>
  )
}

export default PromptComposer
